import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  TableRow,
  CircularProgress,
  TableCell as TableCellBase,
} from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useInView } from 'react-intersection-observer';
import useSearchParams from '../../hooks/useSearchParams';
import { ReactComponent as ArrowDownExpandIcon } from '../../assets/arrow-down-expand.svg';
import useUrlFilters from '../../hooks/useUrlFilters';
import useUserPreferences, {
  TableDensity,
} from '../../hooks/useUserPreferences';
import CheckBox from '../Checkbox';
import { Flex, FlexCenter } from '../Flex';
import OverflowTooltip from '../OverflowTooltip/OverflowTooltip';
import { getValue } from '../../utils/common';
import { componentToken } from '../../style/utils';
import type {
  BaseTableProps,
  Column,
  SortDirection,
  GroupedData,
  FlattenedRow,
} from './types';
import ColumnHeaderActions from './ColumnHeaderActions';
import useColumnDragReorder from './hooks/useColumnDragReorder';
import useStickyColumns from './hooks/useStickyColumns';
import useTableFilteredData from './hooks/useTableFilteredData';
import {
  applySavedColumnOrder,
  getColumnResizeMinWidth,
  getColumnWidth,
} from './utils';
import {
  TABLE_HEAD_HEIGHT,
  MIN_COLUMN_WIDTH,
  SELECTION_COLUMN_WIDTH,
  EXPAND_COLUMN_WIDTH,
  TABLE_ROW_ESTIMATE,
  TABLE_ROW_ESTIMATE_COMPACT,
  GROUP_HEADER_HEIGHT,
  GROUP_HEADER_HEIGHT_COMPACT,
  Wrapper,
  TableWrapper,
  EmptyStateWrapper,
  LoadingWrapper,
  StyledTable,
  TableHead,
  TableBody,
  DraggableHeaderCell,
  TableCell,
  ExpandButtonCell,
  Resizer,
  HeaderCellContent,
  isRowSelectable,
  Row,
  TableLoadingState,
} from './TableShared';

type GroupHeaderContentComponentProps<T> = {
  group: GroupedData<T>;
  isExpanded: boolean;
  hasSelectionColumn?: boolean;
  hasSelectableRows?: boolean;
  selectionState?: 'none' | 'partial' | 'all';
  isSelectionDisabled?: boolean;
  onSelectToggle?: () => void;
};

type GroupHeaderRowComponentProps<T> = GroupHeaderContentComponentProps<T> & {
  onToggle: () => void;
  colSpan: number;
  isFirstGroup?: boolean;
  compact?: boolean;
};

type GroupScrollMarkerRowProps = {
  groupTitle: string;
  onInView: () => void;
};

type GroupLoadingRowProps = {
  groupTitle: string;
  colSpan: number;
};

// Grouped table specific styled components
const GroupHeaderRow = styled(TableRow)<{ $isExpanded?: boolean }>`
  cursor: pointer;
  background-color: ${componentToken('table.groupHeaderRow.backgroundColor')};
  position: sticky;
  top: ${TABLE_HEAD_HEIGHT}px;
  z-index: 1;
  &:hover {
    background-color: ${componentToken(
      'table.groupHeaderRow.hoverBackgroundColor',
    )};
  }
`;

const GroupHeaderCell = styled(TableCellBase)<{
  $isFirstGroup?: boolean;
  $compact?: boolean;
}>`
  &.MuiTableCell-root {
    border: none;
    border-top: ${({ theme, $isFirstGroup }) =>
      $isFirstGroup
        ? `1px solid ${theme.tokens.components.table.groupHeaderCell.borderTop}`
        : 'none'};
    border-bottom: ${({ theme }) =>
      `1px solid ${theme.tokens.components.table.groupHeaderCell.borderBottom}`};
    padding: ${({ $compact }) =>
      $compact
        ? componentToken('table.groupHeaderCell.compactPadding')
        : componentToken('table.groupHeaderCell.padding')};
    font-size: ${componentToken('table.groupHeaderCell.fontSize')};
    font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
    background-color: inherit;
  }
`;

const GroupLoadingCell = styled(TableCellBase)`
  &.MuiTableCell-root {
    border: none;
    padding: ${componentToken('table.groupLoadingCell.padding')};
  }
`;

const FloatingStickyGroupRow = styled(TableRow)`
  cursor: pointer;
  background-color: ${componentToken(
    'table.floatingStickyGroupRow.backgroundColor',
  )};
  position: sticky;
  top: ${TABLE_HEAD_HEIGHT}px;
  z-index: 1;
  &:hover {
    background-color: ${componentToken(
      'table.floatingStickyGroupRow.hoverBackgroundColor',
    )};
  }
`;

const GroupHeaderContent = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: fit-content;
  position: sticky;
  left: 0;
  background-color: inherit;
`;

const GroupHeaderLeft = styled(Flex)<{ $hasSelectionColumn?: boolean }>`
  align-items: center;
  gap: ${componentToken('table.groupHeaderLeft.gap')};
  margin-left: ${({ $hasSelectionColumn }) =>
    $hasSelectionColumn
      ? '0'
      : componentToken('table.groupHeaderLeft.marginLeft')};
`;

const GroupExpandIcon = styled(Flex)<{ $isExpanded?: boolean }>`
  align-items: center;
  color: ${componentToken('table.groupExpandIcon.color')};

  svg {
    width: 14px;
    transition: transform 0.2s ease;
    transform: ${({ $isExpanded }) =>
      $isExpanded ? 'rotate(-180deg)' : 'rotate(-90deg)'};
  }
`;

const GroupIcon = styled(Flex)`
  align-items: center;
  justify-content: center;
  svg {
    width: 18px;
    height: 18px;
  }
`;

const GroupTitle = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  color: ${componentToken('table.groupTitle.color')};
`;

const GroupCount = styled.span`
  font-weight: ${componentToken('table.groupCount.fontWeight')};
  color: ${componentToken('table.groupCount.color')};
  margin-left: ${componentToken('table.groupCount.marginLeft')};
`;

const GroupMetadata = styled(Flex)`
  align-items: center;
  gap: ${componentToken('table.groupMetadata.gap')};
`;

// Group-specific components
const GroupHeaderContentComponent = <T,>({
  group,
  isExpanded,
  hasSelectionColumn,
  hasSelectableRows,
  selectionState,
  isSelectionDisabled,
  onSelectToggle,
}: GroupHeaderContentComponentProps<T>) => (
  <GroupHeaderContent>
    <GroupHeaderLeft $hasSelectionColumn={hasSelectionColumn}>
      {hasSelectionColumn && (
        <CheckBox
          inputProps={{ 'aria-label': `select-group-${group.title}` }}
          sx={{ visibility: hasSelectableRows ? 'visible' : 'hidden' }}
          $noPadding
          checked={selectionState === 'all'}
          indeterminate={selectionState === 'partial'}
          disabled={isSelectionDisabled}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onSelectToggle?.()}
        />
      )}
      <GroupExpandIcon $isExpanded={isExpanded}>
        <ArrowDownExpandIcon />
      </GroupExpandIcon>
      {group.icon && <GroupIcon>{group.icon}</GroupIcon>}
      <Flex>
        <GroupTitle>{group.title}</GroupTitle>
        {group.count !== undefined && <GroupCount>({group.count})</GroupCount>}
      </Flex>
      {group.metadataRenderer && (
        <GroupMetadata>{group.metadataRenderer()}</GroupMetadata>
      )}
    </GroupHeaderLeft>
  </GroupHeaderContent>
);

const GroupHeaderRowComponent = <T,>({
  onToggle,
  colSpan,
  isFirstGroup,
  compact,
  ...contentProps
}: GroupHeaderRowComponentProps<T>) => (
  <GroupHeaderRow
    $isExpanded={contentProps.isExpanded}
    aria-expanded={contentProps.isExpanded}
    onClick={onToggle}
  >
    <GroupHeaderCell
      colSpan={colSpan}
      $isFirstGroup={isFirstGroup}
      $compact={compact}
    >
      <GroupHeaderContentComponent {...contentProps} />
    </GroupHeaderCell>
  </GroupHeaderRow>
);

const GroupScrollMarkerRow = ({
  groupTitle,
  onInView,
}: GroupScrollMarkerRowProps) => {
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView) {
      onInView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <tr>
      <td
        ref={ref}
        aria-label={`scroll-marker-${groupTitle}`}
        style={{ height: 0, padding: 0, border: 'none' }}
      />
    </tr>
  );
};

const GroupLoadingRow = ({ groupTitle, colSpan }: GroupLoadingRowProps) => (
  <tr aria-label={`loading-group-${groupTitle}`}>
    <GroupLoadingCell colSpan={colSpan}>
      <FlexCenter>
        <CircularProgress size={20} />
      </FlexCenter>
    </GroupLoadingCell>
  </tr>
);

export type GroupedTableProps<T> = BaseTableProps<T> & {
  data: Array<GroupedData<T>>;
  defaultExpandedGroups?: boolean;
  expandedGroups?: Record<string, boolean>;
  onGroupToggle?: (groupTitle: string) => void;
  onGroupScrollToBottom?: (groupTitle: string) => void;
  showEmptyGroups?: boolean;
};

const GroupedTable = <T extends object>({
  data: groupedData,
  defaultExpandedGroups = true,
  expandedGroups: controlledExpandedGroups,
  onGroupToggle,
  columns,
  initialSelectedFilters,
  expandedRowRenderer,
  expandCondition,
  selectionColumn,
  selectionDisabled,
  defaultSortKey,
  defaultSortDirection = 'asc',
  emptyStateRenderer,
  selectedItems = [],
  onSelectItem,
  isSelectAll,
  onSelectAllChange,
  excludedItems = [],
  onExcludeItem,
  onFilteredItemsChange,
  idKey,
  sx,
  wrapperSx,
  bottomScrollPadding,
  urlParamsPrefix,
  search,
  searchFunc,
  animateRows,
  focusedRows,
  centerRowCell = true,
  onRowClick,
  localStorageKey,
  isLoading = false,
  onScrollToBottom,
  onGroupScrollToBottom,
  showEmptyGroups = false,
  isControlled,
  isFilterOptionsLoading,
  ...props
}: GroupedTableProps<T>) => {
  const [sortBy, setSortBy] = useState<string | undefined>(defaultSortKey);
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultSortDirection);
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const resizingColRef = useRef<string | null>(null);
  const [resizingCol, setResizingCol] = useState<string | null>(null);

  // Track expanded state for each group (by title)
  const [internalExpandedGroups, setInternalExpandedGroups] = useState<
    Record<string, boolean>
  >({});

  // Initialize expanded groups based on defaultExpandedGroups
  useEffect(() => {
    if (groupedData && !controlledExpandedGroups) {
      setInternalExpandedGroups((prev) => {
        const newState = { ...prev };
        groupedData.forEach((group) => {
          if (newState[group.title] === undefined) {
            newState[group.title] = defaultExpandedGroups;
          }
        });
        return newState;
      });
    }
  }, [groupedData, defaultExpandedGroups, controlledExpandedGroups]);

  const expandedGroups = controlledExpandedGroups ?? internalExpandedGroups;

  const toggleGroupExpanded = useCallback(
    (groupTitle: string) => {
      if (onGroupToggle) {
        onGroupToggle(groupTitle);
        return;
      }
      setInternalExpandedGroups((prev) => ({
        ...prev,
        [groupTitle]: !prev[groupTitle],
      }));
    },
    [onGroupToggle],
  );

  const handleResizeStart = (e: React.MouseEvent, colId: string) => {
    e.preventDefault();
    e.stopPropagation();

    resizingColRef.current = colId;
    setResizingCol(colId);

    const startX = e.pageX;
    const headerCell = (e.currentTarget as HTMLElement).closest('th');
    const startWidth = headerCell?.getBoundingClientRect().width || 0;
    const minWidth = getColumnResizeMinWidth(
      columns.find((c) => c.id === colId),
      MIN_COLUMN_WIDTH,
    );

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (resizingColRef.current !== colId) return;
      const currentX = moveEvent.pageX;
      const diff = currentX - startX;
      const newWidth = Math.max(minWidth, startWidth + diff);

      setColWidths((prev) => ({
        ...prev,
        [colId]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      resizingColRef.current = null;
      setResizingCol(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleResizeDoubleClick = (e: React.MouseEvent, colId: string) => {
    e.preventDefault();
    e.stopPropagation();

    setColWidths((prev) => {
      const newColWidths = { ...prev };
      delete newColWidths[colId];
      return newColWidths;
    });
  };

  const sortKey = useMemo(() => `${urlParamsPrefix}_sort`, [urlParamsPrefix]);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { ref: scrollToBottomItemRef, inView: isScrollToEndItemInView } =
    useInView();

  const visibleColumns = useMemo(
    () => columns.filter((c) => !c.hideColumn),
    [columns],
  );

  const [toggledColumns] = useState(
    visibleColumns.filter((c) => !c.defaultUntoggled).map((c) => c.id),
  );

  const visibleColumnsToShow = useMemo(
    () =>
      columns.filter(
        (c) =>
          visibleColumns.find((vc) => vc.id === c.id) &&
          toggledColumns.find((tc) => tc === c.id),
      ),
    [columns, toggledColumns, visibleColumns],
  );

  // Flatten data for filtering purposes
  const flattenedData = useMemo(
    () => groupedData.flatMap((group) => group.data),
    [groupedData],
  );

  const { filteredData, getFilterOptions } = useTableFilteredData<T>({
    data: flattenedData,
    columns,
    urlParamsPrefix,
    searchValue: search,
    searchFunc,
    isControlled,
  });

  const { urlFilters, addFilter, updateFilter } = useUrlFilters({
    urlKey: urlParamsPrefix,
    initialFilters: initialSelectedFilters,
  });

  const { userPreferences } = useUserPreferences();

  const density: TableDensity =
    (localStorageKey
      ? userPreferences.density?.[localStorageKey]
      : undefined) || 'regular';
  const isCompact = density === 'compact';
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (urlParamsPrefix) {
      const sortParam = searchParams.get(sortKey);
      if (sortParam) {
        try {
          const { id, direction } = JSON.parse(sortParam);
          setSortBy(id);
          setSortDirection(direction as SortDirection);
          // eslint-disable-next-line no-empty
        } catch (e) {}
      }
    }
  }, [urlParamsPrefix, searchParams, sortKey]);

  const handleSort = useCallback(
    (id: string, direction: SortDirection) => {
      setSortBy(id);
      setSortDirection(direction);

      if (urlParamsPrefix) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set(sortKey, JSON.stringify({ id, direction }));
        setSearchParams(newSearchParams);
      }
    },
    [urlParamsPrefix, searchParams, sortKey, setSearchParams],
  );

  const columnsToShow: Column<T>[] = useMemo(() => {
    const storageColumns = localStorageKey
      ? userPreferences.visibleColumns?.[localStorageKey]
      : undefined;

    const filteredColumns = storageColumns
      ? visibleColumns.filter(
          (c1) => c1.nonToggleable || storageColumns.find((c2) => c2 === c1.id),
        )
      : visibleColumnsToShow;

    const savedOrder = localStorageKey
      ? userPreferences.columnOrder?.[localStorageKey]
      : undefined;

    return applySavedColumnOrder(filteredColumns, savedOrder);
  }, [localStorageKey, userPreferences, visibleColumns, visibleColumnsToShow]);

  const {
    draggingColumnId,
    isReordering,
    columnTranslations,
    columnRefs,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useColumnDragReorder({
    columnsToShow,
    localStorageKey,
  });

  const stickyColumns = useStickyColumns({
    columns: columnsToShow,
    colWidths,
    scrollElementRef: tableContainerRef,
    selectionColumnWidth: selectionColumn ? SELECTION_COLUMN_WIDTH : 0,
    expandColumnWidth: expandedRowRenderer ? EXPAND_COLUMN_WIDTH : 0,
  });

  // Helper function to sort an array of data
  const sortDataArray = useCallback(
    (dataToSort: T[]): T[] => {
      if (isControlled) {
        return dataToSort;
      }

      if (sortBy && sortDirection) {
        const compareFunc = columnsToShow.find(
          (c) => c.id === sortBy,
        )?.compareFunc;

        const defaultCompare = (a: T, b: T) => {
          const aLower = getValue(a, sortBy)?.toString().toLowerCase();
          const bLower = getValue(b, sortBy)?.toString().toLowerCase();

          if (!aLower && !bLower) return 0;
          if (!aLower) return 1;
          if (!bLower) return -1;
          if (aLower < bLower) return -1;
          if (aLower > bLower) return 1;
          return 0;
        };

        const sortedAsc = compareFunc
          ? [...dataToSort].sort((a, b) => compareFunc(a, b))
          : [...dataToSort].sort(defaultCompare);

        return sortDirection === 'asc' ? sortedAsc : sortedAsc.reverse();
      }
      return dataToSort;
    },
    [columnsToShow, sortBy, sortDirection, isControlled],
  );

  // Create a Set of filtered item IDs for quick lookup
  const filteredItemIds = useMemo(() => {
    if (!idKey) return new Set<string>();
    return new Set(filteredData.map((item) => getValue(item, idKey)));
  }, [filteredData, idKey]);

  // Sort and filter each group's data
  const sortedGroupedData = useMemo(() => {
    const mappedGroups = groupedData.map((group) => {
      // Filter group data to only include items that passed the filter
      const filteredGroupData = idKey
        ? group.data.filter((item) =>
            filteredItemIds.has(getValue(item, idKey)),
          )
        : group.data;

      return {
        ...group,
        data: sortDataArray(filteredGroupData),
        isUnloaded: group.data.length === 0 && !!group.count,
      };
    });

    return showEmptyGroups
      ? mappedGroups
      : mappedGroups.filter(
          (group) => group.data.length > 0 || group.isUnloaded,
        );
  }, [groupedData, sortDataArray, filteredItemIds, idKey, showEmptyGroups]);

  // Create flattened rows for virtualization
  const flattenedRows = useMemo((): FlattenedRow<T>[] => {
    const rows: FlattenedRow<T>[] = [];

    sortedGroupedData.forEach((group) => {
      // Add group header row
      rows.push({
        type: 'group-header',
        groupTitle: group.title,
        group,
      });

      // Add data rows if group is expanded
      const isExpanded = expandedGroups[group.title] ?? defaultExpandedGroups;
      if (isExpanded) {
        if (group.isLoading && group.data.length === 0) {
          rows.push({
            type: 'group-loading',
            groupTitle: group.title,
          });
        }

        group.data.forEach((item, indexInGroup) => {
          rows.push({
            type: 'data',
            groupTitle: group.title,
            data: item,
            indexInGroup,
          });
        });

        // Add scroll marker at the end of each group if callback is provided
        if (onGroupScrollToBottom) {
          rows.push({
            type: 'group-scroll-marker',
            groupTitle: group.title,
          });
        }
      }
    });

    return rows;
  }, [
    sortedGroupedData,
    expandedGroups,
    defaultExpandedGroups,
    onGroupScrollToBottom,
  ]);

  const showLoadingState = useMemo(
    () => isLoading && sortedGroupedData.length === 0,
    [isLoading, sortedGroupedData.length],
  );

  // Calculate colSpan for group headers
  const groupColSpan =
    columnsToShow.length +
    (selectionColumn ? 1 : 0) +
    (expandedRowRenderer ? 1 : 0);

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: flattenedRows.length,
    overscan: 20,
    estimateSize: (index) => {
      const row = flattenedRows[index];
      if (row?.type === 'group-header')
        return isCompact ? GROUP_HEADER_HEIGHT_COMPACT : GROUP_HEADER_HEIGHT;
      if (row?.type === 'group-scroll-marker') return 0;
      return isCompact ? TABLE_ROW_ESTIMATE_COMPACT : TABLE_ROW_ESTIMATE;
    },
    paddingEnd: bottomScrollPadding,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [isCompact, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();

  const totalSize = rowVirtualizer.getTotalSize();

  // Determine if we need to show a floating sticky group header
  const floatingStickyGroup = useMemo(() => {
    if (virtualRows.length === 0) return null;

    const firstVirtualRow = virtualRows[0];
    const firstFlatRow = flattenedRows[firstVirtualRow.index];

    if (!firstFlatRow) return null;

    // If the first visible row is a group header, no floating header needed
    if (firstFlatRow.type === 'group-header') return null;

    // Find which group this row belongs to
    const groupTitle = firstFlatRow.groupTitle;

    // Check if this group's header is in the visible virtual rows
    const groupHeaderVisible = virtualRows.some((vr) => {
      const flatRow = flattenedRows[vr.index];
      return (
        flatRow?.type === 'group-header' && flatRow.groupTitle === groupTitle
      );
    });

    // If header is not visible, return the group info for floating header
    if (!groupHeaderVisible) {
      const group = sortedGroupedData.find((g) => g.title === groupTitle);
      if (group) {
        return {
          group,
          isExpanded: expandedGroups[groupTitle] ?? defaultExpandedGroups,
        };
      }
    }

    return null;
  }, [
    virtualRows,
    flattenedRows,
    sortedGroupedData,
    expandedGroups,
    defaultExpandedGroups,
  ]);

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;

  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  const isRowSelected = (rowData: T) =>
    (isSelectAll &&
      isRowSelectable(selectionColumn, rowData) &&
      !excludedItems?.includes(getValue(rowData, idKey || 'id'))) ||
    (!isSelectAll && selectedItems?.includes(getValue(rowData, idKey || 'id')));

  const handleRowSelect = (id: string) => {
    if (isSelectAll) {
      if (excludedItems?.includes(id)) {
        onExcludeItem?.(excludedItems.filter((item) => item !== id));
      } else {
        onExcludeItem?.([...excludedItems, id]);
      }
      return;
    }

    if (selectedItems?.includes(id)) {
      onSelectItem?.(selectedItems.filter((item) => item !== id));
    } else {
      onSelectItem?.([...selectedItems, id]);
    }
  };

  useEffect(() => {
    onSelectItem?.([]);
    onExcludeItem?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectAll]);

  const getGroupSelectableIds = useCallback(
    (group: GroupedData<T>) =>
      group.data
        .filter((rowData) => isRowSelectable(selectionColumn, rowData))
        .map((rowData) => getValue(rowData, idKey || 'id')),
    [selectionColumn, idKey],
  );

  const isIdSelected = useCallback(
    (id: string) =>
      isSelectAll ? !excludedItems?.includes(id) : selectedItems?.includes(id),
    [isSelectAll, excludedItems, selectedItems],
  );

  const getGroupSelectionState = useCallback(
    (group: GroupedData<T>) => {
      const groupIds = getGroupSelectableIds(group);
      if (groupIds.length === 0) return 'none';

      const selectedCount = groupIds.filter(isIdSelected).length;
      if (selectedCount === 0) return 'none';
      return selectedCount === groupIds.length ? 'all' : 'partial';
    },
    [getGroupSelectableIds, isIdSelected],
  );

  const handleGroupSelect = useCallback(
    (group: GroupedData<T>) => {
      const groupIds = getGroupSelectableIds(group);
      if (groupIds.length === 0) return;

      const shouldSelect = getGroupSelectionState(group) !== 'all';

      if (isSelectAll) {
        onExcludeItem?.(
          shouldSelect
            ? excludedItems.filter((id) => !groupIds.includes(id))
            : [
                ...excludedItems,
                ...groupIds.filter((id) => !excludedItems.includes(id)),
              ],
        );
        return;
      }

      onSelectItem?.(
        shouldSelect
          ? [
              ...selectedItems,
              ...groupIds.filter((id) => !selectedItems.includes(id)),
            ]
          : selectedItems.filter((id) => !groupIds.includes(id)),
      );
    },
    [
      getGroupSelectableIds,
      getGroupSelectionState,
      isSelectAll,
      excludedItems,
      selectedItems,
      onSelectItem,
      onExcludeItem,
    ],
  );

  useEffect(() => {
    onFilteredItemsChange?.(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData]);

  const tableMinWidth = useMemo(
    () =>
      columnsToShow.reduce((acc, c) => acc + getColumnWidth(c, colWidths), 0),
    [columnsToShow, colWidths],
  );

  useEffect(() => {
    if (isScrollToEndItemInView) {
      onScrollToBottom?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrollToEndItemInView]);

  return (
    <Wrapper sx={sx}>
      <TableWrapper
        ref={tableContainerRef}
        sx={wrapperSx}
        $bottomScrollPadding={bottomScrollPadding}
      >
        <StyledTable sx={{ minWidth: `${tableMinWidth}px` }} {...props}>
          <TableHead $disabled={showLoadingState}>
            <TableRow role="row">
              {selectionColumn && (
                <TableCell
                  sx={{
                    width: `${SELECTION_COLUMN_WIDTH}px`,
                    maxWidth: `${SELECTION_COLUMN_WIDTH}px`,
                  }}
                  key="checkboxColumnHeader"
                  $customVariant="head"
                  $sticky={stickyColumns?.selectionColumn}
                  $selectionColumn
                >
                  <CheckBox
                    inputProps={{ 'aria-label': 'select-all-rows' }}
                    checked={!!isSelectAll}
                    indeterminate={!!isSelectAll && excludedItems?.length > 0}
                    onChange={() => onSelectAllChange?.(!isSelectAll)}
                    disabled={selectionDisabled}
                    $noPadding
                  />
                </TableCell>
              )}
              {columnsToShow.map((c, index) => (
                <DraggableHeaderCell
                  ref={(el: HTMLTableCellElement | null) => {
                    columnRefs.current[c.id] = el;
                  }}
                  sx={{
                    minWidth: colWidths[c.id] || c.width || 'auto',
                    width: colWidths[c.id] || c.width || 'auto',
                    boxSizing: 'border-box',
                  }}
                  aria-labelledby={c.header}
                  role="columnheader"
                  key={c.id}
                  $customVariant="head"
                  $isDragging={draggingColumnId === c.id}
                  $translateX={columnTranslations[c.id] || 0}
                  $isReordering={isReordering}
                  $sticky={stickyColumns?.positions[c.id]}
                  $isDraggable={!!localStorageKey && !c.metaColumn && !c.sticky}
                  draggable={!!localStorageKey && !c.metaColumn && !c.sticky}
                  onDragStart={(e) => handleDragStart(e, c.id)}
                  onDragOver={(e) => handleDragOver(e, c.id, index === 0)}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                >
                  <HeaderCellContent $centerContent={c.centerContent}>
                    <Flex style={{ gap: 12, overflow: 'hidden' }}>
                      <OverflowTooltip
                        color={componentToken('table.groupedTable.color')}
                      >
                        {c.header}
                      </OverflowTooltip>

                      <ColumnHeaderActions
                        column={c}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        getFilterOptions={getFilterOptions}
                        selectedFilters={urlFilters}
                        addFilter={addFilter}
                        updateFilter={updateFilter}
                        isLoading={isFilterOptionsLoading}
                      />
                    </Flex>
                  </HeaderCellContent>
                  {!c.metaColumn && (
                    <Resizer
                      $isResizing={resizingCol === c.id}
                      onMouseDown={(e) => handleResizeStart(e, c.id)}
                      onDoubleClick={(e) => handleResizeDoubleClick(e, c.id)}
                    />
                  )}
                </DraggableHeaderCell>
              ))}
              {!!expandedRowRenderer && (
                <ExpandButtonCell
                  key="expandColumnHeader"
                  $customVariant="head"
                  $sticky={stickyColumns?.expandColumn}
                />
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Floating sticky group header for when the actual group header is virtualized away */}
            {floatingStickyGroup && (
              <FloatingStickyGroupRow
                onClick={() =>
                  toggleGroupExpanded(floatingStickyGroup.group.title)
                }
              >
                <GroupHeaderCell
                  colSpan={groupColSpan}
                  $isFirstGroup={false}
                  $compact={isCompact}
                >
                  <GroupHeaderContentComponent
                    group={floatingStickyGroup.group}
                    isExpanded={floatingStickyGroup.isExpanded}
                    hasSelectionColumn={!!selectionColumn}
                    hasSelectableRows={
                      getGroupSelectableIds(floatingStickyGroup.group).length >
                      0
                    }
                    selectionState={getGroupSelectionState(
                      floatingStickyGroup.group,
                    )}
                    isSelectionDisabled={selectionDisabled}
                    onSelectToggle={() =>
                      handleGroupSelect(floatingStickyGroup.group)
                    }
                  />
                </GroupHeaderCell>
              </FloatingStickyGroupRow>
            )}

            {paddingTop > 0 && (
              <tr>
                <td aria-label="label" style={{ height: `${paddingTop}px` }} />
              </tr>
            )}

            {virtualRows.map((virtualRow) => {
              const flatRow = flattenedRows[virtualRow.index];

              if (!flatRow) return null;

              // Render group header
              if (flatRow.type === 'group-header') {
                const isExpanded =
                  expandedGroups[flatRow.groupTitle] ?? defaultExpandedGroups;
                const isFirstGroup = virtualRow.index === 0;
                return (
                  <GroupHeaderRowComponent
                    key={`group-header-${flatRow.groupTitle}`}
                    group={flatRow.group}
                    isExpanded={isExpanded}
                    onToggle={() => toggleGroupExpanded(flatRow.groupTitle)}
                    colSpan={groupColSpan}
                    isFirstGroup={isFirstGroup}
                    compact={isCompact}
                    hasSelectionColumn={!!selectionColumn}
                    hasSelectableRows={
                      getGroupSelectableIds(flatRow.group).length > 0
                    }
                    selectionState={getGroupSelectionState(flatRow.group)}
                    isSelectionDisabled={selectionDisabled}
                    onSelectToggle={() => handleGroupSelect(flatRow.group)}
                  />
                );
              }

              if (flatRow.type === 'group-loading') {
                return (
                  <GroupLoadingRow
                    key={`group-loading-${flatRow.groupTitle}`}
                    groupTitle={flatRow.groupTitle}
                    colSpan={groupColSpan}
                  />
                );
              }

              // Render scroll marker for infinite scroll
              if (flatRow.type === 'group-scroll-marker') {
                return (
                  <GroupScrollMarkerRow
                    key={`scroll-marker-${flatRow.groupTitle}`}
                    groupTitle={flatRow.groupTitle}
                    onInView={() => {
                      if (onGroupScrollToBottom) {
                        onGroupScrollToBottom(flatRow.groupTitle);
                      }
                    }}
                  />
                );
              }

              // Render data row
              if (flatRow.type === 'data') {
                const rowData = flatRow.data;
                return (
                  <Row
                    key={`${flatRow.groupTitle}-${flatRow.indexInGroup}`}
                    rowData={rowData}
                    columns={columnsToShow}
                    columnTranslations={columnTranslations}
                    stickyColumns={stickyColumns}
                    isReordering={isReordering}
                    onRowClick={onRowClick}
                    expandedRowRenderer={expandedRowRenderer}
                    expandCondition={expandCondition}
                    selectionColumn={selectionColumn}
                    selectionDisabled={selectionDisabled}
                    centerRowCell={centerRowCell}
                    compact={isCompact}
                    isSelected={isRowSelected(rowData)}
                    onSelectChange={() =>
                      handleRowSelect(getValue(rowData, idKey || 'id'))
                    }
                    animate={animateRows?.includes(
                      getValue(rowData, idKey || 'id'),
                    )}
                    isFocused={focusedRows?.includes(
                      getValue(rowData, idKey || 'id'),
                    )}
                    isFirstInGroup={flatRow.indexInGroup === 0}
                  />
                );
              }

              return null;
            })}

            {paddingBottom > 0 && (
              <tr>
                <td
                  aria-label="label"
                  style={{ height: `${paddingBottom}px` }}
                />
              </tr>
            )}

            <tr>
              <td aria-label="scroll-to-bottom" colSpan={columnsToShow.length}>
                <div
                  ref={scrollToBottomItemRef}
                  style={{
                    visibility: 'collapse',
                    width: '100%',
                  }}
                />
              </td>
            </tr>
          </TableBody>
        </StyledTable>

        {!isLoading && sortedGroupedData.length === 0 && (
          <EmptyStateWrapper>{emptyStateRenderer?.()}</EmptyStateWrapper>
        )}

        {showLoadingState && <TableLoadingState minWidth={tableMinWidth} />}

        {isLoading && sortedGroupedData.length > 0 && (
          <LoadingWrapper>
            <CircularProgress size={28} sx={{ my: 1 }} />
          </LoadingWrapper>
        )}
      </TableWrapper>
    </Wrapper>
  );
};

export default GroupedTable;
